import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import {
  fetchDashboardStats,
  fetchTopProfiles,
  fetchTrendingProfiles,
  fetchActiveSpotlightedProfiles,
  fetchFavoriteStats,
  fetchFollowStats,
} from '@/redux/thunks/dashboardThunks';
import { fetchAllAchievements } from '@/redux/thunks/achievementThunks';
import {
  selectDashboardStats,
  selectTopProfiles,
  selectTrendingProfiles,
  selectActiveSpotlightedProfiles,
  selectFavoriteStats,
  selectFollowStats,
  selectStatsLoading,
} from '@/redux/selectors/dashboardSelectors';
import { LoadingSpinner } from '@/components';
import { DashboardStats, TopProfilesCarousel, LeaderboardPreview } from './components';
import './Dashboard.scss';

/**
 * Dashboard Page
 * Main hub for user activity, stats, and feed
 */
const DashboardPage: React.FC = () => {
  const dispatch = useAppDispatch();

  // Selectors
  const stats = useAppSelector(selectDashboardStats);
  const topProfiles = useAppSelector(selectTopProfiles);
  const trendingProfiles = useAppSelector(selectTrendingProfiles);
  const spotlightedProfiles = useAppSelector(selectActiveSpotlightedProfiles);
  const favoriteStats = useAppSelector(selectFavoriteStats);
  const followStats = useAppSelector(selectFollowStats);
  const isLoading = useAppSelector(selectStatsLoading);
  const currentUsername = useAppSelector((s) => s.auth.user?.username);

  // Fetch initial data on mount
  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchTopProfiles(5));
    dispatch(fetchTrendingProfiles(10));
    dispatch(fetchActiveSpotlightedProfiles(8));
    dispatch(fetchFavoriteStats());
    dispatch(fetchFollowStats());
    if (currentUsername) {
      dispatch(fetchAllAchievements(currentUsername));
    }
    // Feed data is fetched lazily by KlyxFeed component based on active tab
  }, [dispatch, currentUsername]);

  if (isLoading && !stats) {
    return (
      <div className="dashboard-page">
        <LoadingSpinner size="medium" message="Loading your dashboard..." fullHeight />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        {/* Dashboard Header */}
        <header className="dashboard-header">
          <h1>Your Dashboard</h1>
          <p>Welcome to your Klyx Hub</p>
        </header>

        {/* Stats Cards */}
        <DashboardStats
          stats={stats}
          favoriteStats={favoriteStats}
          followStats={followStats}
        />

        {/* Top Profiles Carousel */}
        <TopProfilesCarousel profiles={topProfiles} title="Top Profiles" />

        {/* Hot Now — driven by HotScore (45-min decay + post-trade penalty).
            See server/src/services/HotScoringService.ts for the math. */}
        {trendingProfiles.length > 0 && (
          <TopProfilesCarousel profiles={trendingProfiles} title="Hot Now" />
        )}

        {/* Spotlighted Right Now — paid placements currently live.
            Source attribution is 'spotlight' so analytics can credit
            the click to the specific promotion (profile.id is the
            spotlight_promotion.id, passed as ref). */}
        {spotlightedProfiles.length > 0 && (
          <TopProfilesCarousel
            profiles={spotlightedProfiles}
            title="Spotlighted Right Now"
            source="spotlight"
          />
        )}

        {/* Leaderboard preview — top KlyxValue in caller's home area
            (falls back to global if no home set). Highlights the
            caller's own row + links to the full leaderboard. */}
        <LeaderboardPreview />
      </div>
    </div>
  );
};

export default DashboardPage;
